import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export const PricingSection = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);
  const discount = 0.16; // 16% discount for annual plans

  const basePrices = {
    starter: 0,
    essential: 49,
    catalyst: 99,
    pro: 149
  };

  const getPrice = (basePrice: number) => {
    if (basePrice === 0) return "Free";
    if (!isAnnual) return `$${basePrice}`;
    // For annual, show the monthly price but billed annually
    const annualPrice = basePrice * (1 - discount);
    return `$${Math.round(annualPrice)}`;
  };

  const getBillingText = (basePrice: number) => {
    if (basePrice === 0) return "";
    if (!isAnnual) return "/month";
    return "/month";
  };

  const getSubText = (basePrice: number) => {
    if (basePrice === 0) return "";
    if (!isAnnual) return "";
    return "Billed annually";
  };

  const tiers = [
    {
      id: "free",
      name: "Cause Starter",
      basePrice: basePrices.starter,
      description: "Perfect for nonprofits laying the foundation for volunteer engagement.",
      features: [
        "Interactive volunteer booking & registration page (co-branded)",
        "Community Events: Host 1 campaign & 1 community event",
        "Corporate Partnerships: Feature up to 3 live events for Booking Catalog",
        "AI Growth Engine: Basic content creation support",
        "AI Impact Assistant: Basic query responses & lead capture",
      ],
      cta: "Get Started Free",
      highlighted: false
    },
    {
      id: "essential",
      name: "Cause Essential",
      basePrice: basePrices.essential,
      description: "Designed for growing nonprofits expanding engagement.",
      features: [
        "All Starter features, plus:",
        "Community Events: Run up to 3 campaigns & 3 community events",
        "Corporate Partnerships: Feature up to 6 live events in the Booking Catalog",
        "AI Growth Engine: Advanced content creation tools",
        "AI Impact Assistant: Advanced query responses & lead capture",
        "AI Knowledge Base: Smart responses for listed events and campaigns",
      ],
      cta: "Start Essential Plan",
      highlighted: true
    },
    {
      id: "catalyst",
      name: "Cause Catalyst",
      basePrice: basePrices.catalyst,
      description: "For nonprofits scaling their impact with AI-powered automation.",
      features: [
        "All Essential features, plus:",
        "Community Events: Run up to 5 campaigns & 5 community events",
        "Corporate Partnerships: Feature up to 10 live events via Booking Catalog",
        "AI Growth Engine: Enhanced adaptive AI content creation",
        "AI Impact Assistant: Enhanced query responses & lead intent capture",
        "AI Knowledge Base: Expanded coverage for smarter responses",
        "Impact Hub: Remove CauseConnect branding from interactive pages",
        "Support: Standard email support"
      ],
      cta: "Start Catalyst Plan",
      highlighted: false
    },
    {
      id: "pro",
      name: "Cause Pro",
      basePrice: basePrices.pro,
      description: "For high-growth nonprofits requiring advanced AI capabilities.",
      features: [
        "All Catalyst features, plus:",
        "Community Events: Run unlimited campaigns & community events",
        "Corporate Partnerships: Feature unlimited live events via Booking Catalog",
        "AI Growth Engine: Advanced adaptive AI content creation",
        "AI Impact Assistant: Advanced query responses & lead intent capture",
        "AI Knowledge Base: Advanced coverage for smarter responses",
        "Get Priority access to all new upcoming features",
        "Support: Priority support"
      ],
      cta: "Start Pro Plan",
      highlighted: false
    }
];

return (
  <section className="py-16 md:py-24 bg-[#f8fff0] overflow-hidden">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">Pricing Plans</h2>
          <p className="text-base md:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Save time, automate busywork, and maximize your nonprofit’s impact with AI-powered tools.
          </p>

        </motion.div>

          {/* Billing Toggle */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-4 bg-white/80 backdrop-blur-sm rounded-full p-1.5 shadow-lg mb-8"
          >
            <button
              onClick={() => setIsAnnual(false)}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200",
                !isAnnual
                  ? "bg-[#2E7D32] text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 relative",
                isAnnual
                  ? "bg-[#2E7D32] text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              Yearly
              {isAnnual && (
                <motion.span 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-3 -right-3 bg-emerald-500 text-white text-xs font-medium px-2 py-0.5 rounded-full"
                >
                  -16%
                </motion.span>
              )}
            </button>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {tiers.map((tier, index) => (
            <motion.div 
              key={index} 
              className={cn(
                "bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative",
                tier.highlighted && "border-2 border-[#2E7D32] transform scale-105 bg-white"
              )}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#2E7D32] text-white text-xs font-bold px-3 py-1 rounded-full">
                    BEST VALUE
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2 tracking-tight">{tier.name}</h3>
                <motion.div 
                  key={isAnnual ? 'annual' : 'monthly'}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="mb-3"
                >
                  <div className="text-4xl font-bold">
                    {getPrice(tier.basePrice)}
                    {tier.basePrice > 0 && (
                      <span className="text-lg text-gray-500 font-normal">
                        {getBillingText(tier.basePrice)}
                      </span>
                    )}
                  </div>
                  {tier.basePrice > 0 && (
                    <div className="text-sm text-gray-500 mt-1">
                      {getSubText(tier.basePrice)}
                    </div>
                  )}
                </motion.div>
                <p className="text-sm text-gray-600 leading-relaxed">{tier.description}</p>
              </div>

              <Button 
                className={cn(
                  "w-full mb-8 py-6 font-medium tracking-wide transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]",
                  tier.highlighted 
                    ? "bg-[#2E7D32] hover:bg-[#1B5E20] text-white shadow-lg" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                )}
                onClick={() => {
                  const planId = tier.id;
                  const interval = isAnnual ? "annual" : "monthly";
                  navigate(`/dashboard/subscription?plan=${planId}&interval=${interval}`);
                }}
              >
                {tier.cta}
              </Button>

              <div className="space-y-4">
                {tier.features.map((feature, featureIndex) => (
                  <motion.div 
                    key={featureIndex} 
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + featureIndex * 0.1 }}
                  >
                    <Check className="w-5 h-5 text-[#2E7D32] mt-1 flex-shrink-0" />
                    <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
